/**
 * Central de Projetos: painel inicial (ADR-0089).
 *
 * Responde a três perguntas, e só a elas:
 * 1. O que está atrasado ou travado, e por quê?
 * 2. O que cada projetista tem na mão agora?
 * 3. Em que pé está cada cliente, e o que falta para andar?
 *
 * Sem gráfico decorativo e sem as 48 colunas da planilha. Os dados vêm prontos
 * do servidor (foto do Gerenciador + análises); abrir a tela não chama IA.
 */
import React from "react";
import { FiCheckSquare, FiMessageCircle, FiMessageSquare, FiRefreshCw, FiSettings } from "react-icons/fi";
import { useNavigate } from "react-router-dom";

import { PageLoader } from "../../components/LoadingMarcenaria";
import { useAuth } from "../../context/AuthContext";
import { lerPainel } from "../../services/centralProjetos";
import {
  ETAPAS_DO_PROJETO,
  ROTULO_ESPERA,
  descreverStatus,
  quandoFoi,
  resumoDoProjetista,
  textoAtraso,
  tomDoCliente,
} from "./apresentacao";
import { ErroDaCentral, PERM, useCarga } from "./comum";
import {
  Acoes,
  Botao,
  Coluna,
  Faixa,
  Grade,
  Item,
  Linha,
  Lista,
  Pagina,
  Ponto,
  Principal,
  Secao,
  SecaoTitulo,
  Secundario,
  Titulo,
  Topo,
  Vazio,
} from "./styles";

const COLUNAS_ATRASO = "minmax(0, 2fr) minmax(0, 1fr) minmax(0, 2.4fr) 110px";
const COLUNAS_CLIENTE = "minmax(0, 2fr) minmax(0, 1fr) minmax(0, 3fr) minmax(0, 1.4fr) 90px";

function abrirComTeclado(acao) {
  return (evento) => {
    if (evento.key === "Enter" || evento.key === " ") {
      evento.preventDefault();
      acao();
    }
  };
}

function FaixaDeStatus({ status }) {
  const { tom, monitor, detalhes } = descreverStatus(status);
  return (
    <Faixa $tom={tom} role="status">
      <span>
        <Ponto $tom={tom} aria-hidden="true" />
        <strong>{monitor}</strong>
      </span>
      {detalhes.map((texto) => (
        <span key={texto}>{texto}</span>
      ))}
    </Faixa>
  );
}

function Atrasados({ linhas, abrir }) {
  return (
    <Secao aria-labelledby="bloco-atrasados">
      <SecaoTitulo>
        <h2 id="bloco-atrasados">Atrasados e travados</h2>
        <span>{linhas.length}</span>
      </SecaoTitulo>
      {linhas.length === 0 ? (
        <Lista><Vazio>Nada atrasado nem travado agora.</Vazio></Lista>
      ) : (
        <Lista>
          {linhas.map((l) => (
            <Item key={`${l.numero_pedido}-${l.ambiente}-${l.motivo}`}>
              <Linha
                $colunas={COLUNAS_ATRASO}
                role="link"
                tabIndex={0}
                onClick={() => abrir(l.numero_pedido)}
                onKeyDown={abrirComTeclado(() => abrir(l.numero_pedido))}
              >
                <Principal title={`${l.cliente} · ${l.ambiente}`}>
                  <Ponto $tom={l.dias > 0 ? "danger" : "warning"} aria-hidden="true" />
                  {l.cliente} {l.ambiente ? <small>· {l.ambiente}</small> : null}
                </Principal>
                <Secundario>{l.projetista || "Sem projetista"}</Secundario>
                <Secundario title={l.motivo}>{l.motivo}</Secundario>
                <Secundario $tom={l.dias > 0 ? "danger" : "warning"}>
                  {l.dias > 0 ? textoAtraso(l.dias) : "travado"}
                </Secundario>
              </Linha>
            </Item>
          ))}
        </Lista>
      )}
    </Secao>
  );
}

function PorProjetista({ colunas }) {
  return (
    <Secao aria-labelledby="bloco-projetistas">
      <SecaoTitulo>
        <h2 id="bloco-projetistas">Por projetista</h2>
      </SecaoTitulo>
      {colunas.length === 0 ? (
        <Lista><Vazio>Nenhum ambiente em projeto agora.</Vazio></Lista>
      ) : (
        <Grade>
          {colunas.map((coluna) => (
            <Coluna key={coluna.projetista}>
              <h3>{coluna.projetista}</h3>
              <Secundario>{resumoDoProjetista(coluna) || "Nada na mão"}</Secundario>
              {ETAPAS_DO_PROJETO.filter(({ valor }) => (coluna[valor] || []).length > 0).map(({ valor, rotulo }) => (
                <details key={valor}>
                  <summary>{rotulo}</summary>
                  <ul>
                    {coluna[valor].map((nome) => (
                      <li key={nome} title={nome}>{nome}</li>
                    ))}
                  </ul>
                </details>
              ))}
            </Coluna>
          ))}
        </Grade>
      )}
    </Secao>
  );
}

function Clientes({ clientes, abrir }) {
  return (
    <Secao aria-labelledby="bloco-clientes">
      <SecaoTitulo>
        <h2 id="bloco-clientes">Clientes</h2>
        <span>{clientes.length}</span>
      </SecaoTitulo>
      {clientes.length === 0 ? (
        <Lista><Vazio>Nenhum cliente com ambiente em projeto.</Vazio></Lista>
      ) : (
        <Lista>
          {clientes.map((c) => {
            const tom = tomDoCliente(c);
            const proximo = c.resumo || (c.divergencias[0]?.descricao ?? "");
            return (
              <Item key={c.numero_pedido}>
                <Linha
                  $colunas={COLUNAS_CLIENTE}
                  role="link"
                  tabIndex={0}
                  onClick={() => abrir(c.numero_pedido)}
                  onKeyDown={abrirComTeclado(() => abrir(c.numero_pedido))}
                >
                  <Principal title={c.nome}>
                    <Ponto $tom={tom} aria-hidden="true" />
                    {c.nome} <small>· {c.numero_pedido}</small>
                  </Principal>
                  <Secundario $tom={tom === "danger" ? "danger" : undefined}>
                    {c.etapa_rotulo}
                    {c.atraso_dias > 0 ? ` · ${textoAtraso(c.atraso_dias)}` : ""}
                  </Secundario>
                  <Secundario title={proximo}>{proximo || "Sem análise ainda."}</Secundario>
                  <Secundario>{ROTULO_ESPERA[c.quem_espera] || ""}</Secundario>
                  <Secundario title="Última mensagem">{quandoFoi(c.ultima_mensagem_em)}</Secundario>
                </Linha>
              </Item>
            );
          })}
        </Lista>
      )}
    </Secao>
  );
}

export default function PainelCentral() {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const { dados, erro, carregando, recarregar } = useCarga((request) => lerPainel(request), []);
  const abrir = (pedido) => navigate(`/central-projetos/clientes/${encodeURIComponent(pedido)}`);

  return (
    <Pagina>
      <Topo>
        <Titulo>
          <h1>Central de Projetos</h1>
          <p>Atrasos, o que cada projetista tem na mão e em que pé está cada cliente.</p>
        </Titulo>
        <Acoes>
          <Botao type="button" data-icone-no-celular="true" onClick={() => navigate("/central-projetos/pendencias")}>
            <FiCheckSquare aria-hidden="true" /> <span>Pendências</span>
          </Botao>
          <Botao type="button" data-icone-no-celular="true" onClick={() => navigate("/central-projetos/conversas")}>
            <FiMessageCircle aria-hidden="true" /> <span>Conversas</span>
          </Botao>
          {hasPermission?.(PERM.ASSISTENTE) ? (
            <Botao type="button" data-icone-no-celular="true" onClick={() => navigate("/central-projetos/assistente")}>
              <FiMessageSquare aria-hidden="true" /> <span>Assistente</span>
            </Botao>
          ) : null}
          {hasPermission?.(PERM.CONFIGURAR) ? (
            <Botao type="button" aria-label="Configuração" title="Configuração" onClick={() => navigate("/central-projetos/configuracao")}>
              <FiSettings aria-hidden="true" />
            </Botao>
          ) : null}
          <Botao type="button" aria-label="Atualizar" title="Atualizar" onClick={recarregar} disabled={carregando}>
            <FiRefreshCw aria-hidden="true" />
          </Botao>
        </Acoes>
      </Topo>

      {erro ? <ErroDaCentral erro={erro} /> : null}
      {carregando && !dados ? <PageLoader scope="central-projetos" /> : null}
      {dados ? (
        <>
          <FaixaDeStatus status={dados.status} />
          <Atrasados linhas={dados.atrasados || []} abrir={abrir} />
          <PorProjetista colunas={dados.por_projetista || []} />
          <Clientes clientes={dados.clientes || []} abrir={abrir} />
        </>
      ) : null}
    </Pagina>
  );
}
